import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconPort } from "@/assets/icons/geist/IconPort";
import { IconSSH } from "@/assets/icons/geist/IconSSH";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { OpenPortPopover } from "@/components/environments/OpenPortForm";
import { WithPopover } from "@/components/environments/WithPopover";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { KeyboardKey } from "@/components/KeyboardKey";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv, useSetEditorIdForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainEditor } from "@/queries/editor-queries";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";
import { canOpen } from "@/routes/environments/phase";
import { isMacLike } from "@/utils/platform";
import {
    forwardRef,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type HTMLAttributes,
    type PropsWithChildren,
} from "react";

export const ActionBar: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);

    return (
        <div className="items-center justify-center" data-testid="action-bar">
            {environment && (
                <div className="animate-in fade-in slide-in-from-top" translate="no">
                    <Island environment={environment} />
                </div>
            )}
        </div>
    );
};

const Island: FC<PropsWithClassName & { environment: PlainEnvironment }> = ({ environment, className }) => {
    const openEditor = useOpenEditor();
    const {
        selectedEditor,
        isSingleEditor,
        availableEditors,
        isPending: isPendingEditors,
    } = useGetEditorForEnv(environment.id);

    const anchor = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const openable = useMemo(() => {
        return !!environment && canOpen(environment);
    }, [environment]);

    useOpenEditorShortcut((triggered) => {
        if (triggered && selectedEditor) {
            void openEditor(selectedEditor, environment);
        }
    });
    useEffect(() => {
        setOpen((current) => {
            if (current !== openable) {
                return openable;
            }
            return current;
        });
    }, [openable]);

    useEffect(() => {
        setOpen(!!environment && canOpen(environment));
    }, [environment]);

    const isMac = isMacLike();

    return (
        <>
            <div
                ref={anchor}
                data-testid="action-bar-island"
                className={cn(
                    "inline-flex h-12 items-center justify-center gap-1 rounded-xl border-[0.5px] border-border-base bg-surface-pure px-1.5 py-4 shadow shadow-black/10",
                    className,
                )}
            >
                {open && !window.isWebview && (
                    <div
                        className="flex items-center rounded-lg p-0 hover:text-content-primary hover:has-[>:not(button:last-child):hover]:bg-surface-03"
                        data-testid="open-in-default-editor"
                    >
                        <SkeletonBlock ready={!!selectedEditor && !isPendingEditors}>
                            <Tooltip
                                sideOffset={4}
                                side="left"
                                content={
                                    <div className="flex flex-row items-center gap-2">
                                        Open editor
                                        <KeyboardKey className="ml-1 bg-surface-invert-tertiary">
                                            {isMac ? "⌘" : "Ctrl"}
                                        </KeyboardKey>
                                        <KeyboardKey className="bg-surface-invert-tertiary">
                                            {isMac ? "⇧" : "Shift"}
                                        </KeyboardKey>
                                        <KeyboardKey className="bg-surface-invert-tertiary">A</KeyboardKey>
                                    </div>
                                }
                            >
                                {selectedEditor && (
                                    <button
                                        className={"flex items-center gap-2 py-2 pl-2 pr-1"}
                                        onClick={() => openEditor(selectedEditor, environment)}
                                    >
                                        <>
                                            <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />
                                            <div className="text-sm font-medium">Open {selectedEditor?.name}</div>
                                        </>
                                    </button>
                                )}
                            </Tooltip>
                        </SkeletonBlock>
                        {!isSingleEditor && (
                            <WithPopover
                                anchor={anchor}
                                button={
                                    <Button
                                        variant={"ghost"}
                                        data-testid="open-editor-selection-popover"
                                        className={cn("rounded-l-none rounded-r-lg border-0 p-1 transition-none")}
                                        LeadingIcon={IconChevronDownSmall}
                                    />
                                }
                                data-track-location={TrackLocations.EnvironmentEditorPopover}
                                hideCloseButton={true}
                            >
                                <EditorSelectionPopover environment={environment} editors={availableEditors || []} />
                            </WithPopover>
                        )}
                    </div>
                )}
                {open && (
                    <WithPopover
                        anchor={anchor}
                        button={
                            <IslandButton label="SSH" data-testid="open-ssh">
                                <IconSSH size="sm" />
                            </IslandButton>
                        }
                        data-track-location={TrackLocations.EnvironmentSSHFormPopover}
                    >
                        <SSHForm environment={environment} />
                    </WithPopover>
                )}
                {open && (
                    <OpenPortPopover
                        anchor={anchor}
                        button={
                            <IslandButton label="Open Port" data-testid="open-public-ports">
                                <IconPort size="sm" className="shrink-0" />
                            </IslandButton>
                        }
                        environmentId={environment.id}
                    />
                )}
                <EnvironmentActionsDropdown
                    environment={environment}
                    defaultEditor={selectedEditor}
                    button={
                        <Button
                            variant="ghost"
                            className="rounded-lg border-0 hover:bg-surface-03 hover:text-content-primary"
                            data-testid="open-more-actions"
                            aria-label={`More actions`}
                        >
                            <IconEllipsisHorizontal size="base" className={cn("size-6 p-0.5")} />
                        </Button>
                    }
                />
            </div>
        </>
    );
};

const IslandButton = forwardRef<
    HTMLButtonElement,
    HTMLAttributes<HTMLButtonElement> &
        PropsWithClassName &
        PropsWithChildren & {
            label: string;
            tooltip?: string;
        }
>(({ label, children, className, ...props }, ref) => {
    return (
        <button
            ref={ref}
            className={cn(
                "flex items-center justify-start gap-1 rounded-lg p-2 hover:bg-surface-03 hover:text-content-primary",
                className,
            )}
            {...props}
        >
            {children}
            <div className="text-sm font-medium leading-tight">{label}</div>
        </button>
    );
});
IslandButton.displayName = "IslandButton";

type SSHFormProps = {
    environment: PlainEnvironment;
};
const SSHForm: FC<SSHFormProps> = ({ environment }) => {
    return (
        <div className="flex w-[500px] flex-col gap-4 p-5">
            <div>
                <Heading2>Connect via SSH</Heading2>
                <Text muted className="text-base">
                    How to connect via SSH from your local machine using the CLI
                </Text>
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <p className="font-bold">Setup SSH host config</p>
                    <CopyableInput value={"gitpod env ssh-config"} />
                </div>
                <div className="flex flex-col gap-1">
                    <p className="font-bold">Connect to devcontainer</p>
                    <CopyableInput value={`ssh ${environment.id}.gitpod.environment`} />
                </div>
            </div>
            <div className="-mx-1 my-1 h-px bg-content-tertiary bg-content-tertiary/20" />
            <p className="text-sm text-content-secondary">Documentation</p>
            <div className="flex flex-col gap-2">
                <div>
                    <ExternalLink href="https://www.gitpod.io/docs/flex/integrations/cli">Install the CLI</ExternalLink>
                </div>
                <div>
                    <ExternalLink href="https://www.gitpod.io/docs/flex/editors/jetbrains">
                        Connect via JetBrains Gateway
                    </ExternalLink>
                </div>
                <div>
                    <ExternalLink href="https://www.gitpod.io/docs/flex/integrations/cli#ssh-configuration">
                        SSH Configuration
                    </ExternalLink>
                </div>
            </div>
        </div>
    );
};

type EditorSelectionPopoverProps = {
    environment: PlainEnvironment;
    editors: PlainEditor[];
    onClose?: () => void;
};
const EditorSelectionPopover: FC<EditorSelectionPopoverProps> = ({ environment, editors, onClose }) => {
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);
    const openEditor = useOpenEditor();

    return (
        <div className="flex w-[410px] gap-4 p-4">
            <SkeletonBlock ready={!!editors} className="h-[250px]">
                <div>
                    <div className="flex flex-wrap gap-2 pb-2">
                        {editors.map((editor, i) => (
                            <EditorButton
                                key={`${editor.id}-${i}-${editor.id}`}
                                editor={editor}
                                onClick={() => {
                                    void openEditor(editor, environment);
                                    void setEditorForEnv(editor.id);
                                    onClose?.();
                                }}
                            />
                        ))}
                    </div>
                    <p className="p-1 text-sm font-medium text-content-secondary">
                        You can change your default editor in{" "}
                        <Link to={`/settings/preferences`} className="text-content-link" onClick={onClose}>
                            Settings &gt; Preferences
                        </Link>
                    </p>
                    <p className="p-1 text-sm font-medium text-content-secondary">
                        Having trouble opening your IDE?{" "}
                        <ExternalLink
                            href={"https://www.gitpod.io/docs/flex/editors"}
                            className="text-sm"
                            onClick={onClose}
                        >
                            Learn more.
                        </ExternalLink>
                    </p>
                </div>
            </SkeletonBlock>
        </div>
    );
};

const EditorButton: FC<{ editor: PlainEditor; onClick: () => void }> = ({ editor, onClick }) => {
    return (
        <Button
            variant="ghost"
            className="h-[36px] gap-2 rounded-lg border-2 border-surface-02 bg-surface-02 p-2 hover:border-content-secondary hover:bg-surface-02"
            onClick={onClick}
        >
            <EditorIcon editor={editor} size="base" />
            <span>{editor.name}</span>
        </Button>
    );
};
